import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Search, ChevronRight, Loader2, MapPin, Briefcase,
  Scissors, Camera, GraduationCap, Stethoscope, Home, Brain,
  ShoppingBag, Sparkles, Hammer, Star, Zap, Droplets,
  Flower, HeartPulse, Palette, Code, Calculator, Wrench,
  Scale, Utensils, Car, PenTool, Monitor, Hand, TreePine,
  Dumbbell, KeyRound, PaintBucket, Apple, Ruler, FileText,
  Pill, Building, Shield, Sun, Thermometer, Truck, Circle,
  Baby, Globe, Languages, Shirt, Cake, PartyPopper, Music,
  Video, Printer, Megaphone, Heart, MapPin as MapPinIcon,
  Grid3x3, DoorOpen, Fence
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import CatalogConcierge from '@/components/CatalogConcierge';
import { API } from '@/utils/api';

const ICON_MAP = {
  'scissors': Scissors, 'camera': Camera, 'graduation-cap': GraduationCap,
  'stethoscope': Stethoscope, 'home': Home, 'brain': Brain,
  'shopping-bag': ShoppingBag, 'sparkles': Sparkles, 'hammer': Hammer,
  'star': Star, 'zap': Zap, 'droplets': Droplets, 'flower': Flower,
  'heart-pulse': HeartPulse, 'palette': Palette, 'code': Code,
  'calculator': Calculator, 'wrench': Wrench, 'scale': Scale,
  'utensils': Utensils, 'car': Car, 'pen-tool': PenTool,
  'monitor': Monitor, 'hand': Hand, 'tree-pine': TreePine,
  'dumbbell': Dumbbell, 'key-round': KeyRound, 'paint-bucket': PaintBucket,
  'apple': Apple, 'ruler': Ruler, 'file-text': FileText,
  'pill': Pill, 'building': Building, 'shield': Shield,
  'sun': Sun, 'thermometer': Thermometer, 'truck': Truck,
  'circle': Circle, 'baby': Baby, 'globe': Globe,
  'languages': Languages, 'shirt': Shirt, 'cake': Cake,
  'party-popper': PartyPopper, 'music': Music, 'video': Video,
  'printer': Printer, 'megaphone': Megaphone, 'heart': Heart,
  'map-pin': MapPinIcon, 'grid': Grid3x3, 'door-open': DoorOpen,
  'fence': Fence, 'search': Search,
};

export default function BusinessCatalogIndex() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/katalog`).then(res => {
      setData(res.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const { categories = [], cities = [], seo = {} } = data || {};
  const filtered = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.plural.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://apisai.pl${seo.canonical}`} />
        {seo.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
        {seo.og && (
          <>
            <meta property="og:title" content={seo.og.title} />
            <meta property="og:description" content={seo.og.description} />
            <meta property="og:type" content={seo.og.type} />
            <meta property="og:url" content={seo.og.url} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.og.title} />
            <meta name="twitter:description" content={seo.og.description} />
          </>
        )}
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/rejestracja">
              <Button data-testid="nav-register-btn" className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-full px-6 text-sm">
                Dodaj swoją firmę
              </Button>
            </Link>
          </div>
        </nav>

        <div className="pt-28 pb-20">
          <div className="max-w-6xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
                  <Briefcase className="w-4 h-4" />
                  Katalog Firm
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="catalog-title">
                  {seo.h1}
                </h1>
                <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                  Znajdź sprawdzonego specjalistę w swoim mieście. Od groomerów po budowlańców.
                </p>
              </motion.div>
            </div>

            {/* Search */}
            <div className="max-w-lg mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  data-testid="catalog-search"
                  type="text"
                  placeholder="Szukaj kategorii..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50"
                />
              </div>
            </div>

            {/* AI Koncierż — tryb „firmy" (zablokowany, bez przełącznika) */}
            <div className="max-w-4xl mx-auto mb-12" data-testid="business-concierge">
              <CatalogConcierge lockMode="firmy" />
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-16" data-testid="categories-grid">
              {filtered.map((cat, i) => {
                const IconComp = ICON_MAP[cat.icon] || Briefcase;
                return (
                  <motion.div key={cat.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <Link
                      to={`/katalog/${cat.slug}`}
                      data-testid={`category-${cat.slug}`}
                      className="block p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-3 group-hover:bg-emerald-500/20 transition-colors">
                        <IconComp className="w-5 h-5 text-emerald-400" />
                      </div>
                      <h3 className="font-semibold text-white text-sm mb-1 group-hover:text-emerald-400 transition-colors">{cat.plural}</h3>
                      <p className="text-xs text-gray-500">{cat.count} firm</p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {/* Miasta */}
            <div className="mb-16">
              <h2 className="text-xl font-bold text-white mb-6">Popularne miasta</h2>
              <div className="flex flex-wrap gap-2" data-testid="cities-list">
                {cities.map(city => (
                  <Link
                    key={city.slug}
                    to={`/katalog/miasto/${city.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <MapPin className="w-3 h-3" />
                    {city.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
              <h2 className="text-2xl font-bold text-white mb-3">Prowadzisz firmę?</h2>
              <p className="text-gray-400 mb-6">Dodaj się do katalogu za darmo i zdobywaj nowych klientów.</p>
              <Link to="/rejestracja">
                <Button data-testid="cta-register" className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8">
                  Dodaj swoją firmę za darmo
                </Button>
              </Link>
            </div>

            {/* Internal links */}
            <div className="mt-12">
              <h2 className="text-lg font-bold text-white mb-4">Powiązane</h2>
              <div className="flex flex-wrap gap-2">
                <Link to="/rasy" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Katalog ras psów
                </Link>
                <Link to="/reproduktory" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Reproduktory
                </Link>
                <Link to="/wizytowki" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Wizytówki online
                </Link>
              </div>
            </div>
          </div>
        </div>

        <ApisAIFooter variant="light" pageType="catalog" pageSlug="katalog" />
      </div>
    </>
  );
}
